import type { ChauffeurPage } from "../slug-chauffeur";

import heroImage from "@/assets/images/chauffeur/transfert/chauffeur-transfert.png";
import { cityPriceFaq } from "../slugs/cityPricing";

const frejusEn: ChauffeurPage = {
  slug: "frejus",
  title: "VTC & private chauffeur in Fréjus",
  subtitle:
    "Private chauffeur in Fréjus: Nice airport transfers (~1 h), Saint-Raphaël TGV station shuttle and chauffeur hire from the Roman city to the beaches, in the Var, 7 days a week.",
  keywords: [
    "vtc fréjus",
    "private chauffeur frejus",
    "frejus var chauffeur",
    "frejus nice airport transfer",
  ],
  hero: {
    title: "VTC & private chauffeur in Fréjus",
    subtitle:
      "The Var's Roman city, from the arena to the Base Nature and Port-Fréjus: Nice airport transfers (~1 h) and TGV station shuttle, 24/7.",
    keywords: [
      "VTC Fréjus",
      "Roman-city chauffeur",
      "Nice airport transfer",
      "Saint-Raphaël TGV station",
    ],
    imageSrc: heroImage.src,
    imageAlt: "Private chauffeur & VTC in Fréjus, the Var's Roman city",
    badges: [
      "Nice airport ~1 h",
      "Saint-Raphaël TGV station",
      "Bilingual chauffeurs",
    ],
  },
  benefits: {
    heading: "Why a chauffeur in Fréjus",
    items: [
      {
        title: "From the Roman city to the beaches",
        description:
          "The arena, episcopal complex and Porte des Gaules on one side, Fréjus-Plage and Port-Fréjus on the other: the chauffeur links upper town and seafront with no parking.",
      },
      {
        title: "Nice airport in ~1 h",
        description:
          "Around an hour via the A8, further than Cannes: anticipated margin and flight tracking as standard.",
      },
      {
        title: "TGV station close by",
        description:
          "Neighbouring Saint-Raphaël-Valescure station puts the TGV minutes away: a dedicated shuttle from Fréjus.",
      },
      {
        title: "Space and large groups",
        description:
          "Base Nature, sports events and seminars: van and VIP minibus available, rated 5.0/5 from 38 Google reviews.",
      },
    ],
  },
  services: {
    heading: "Chauffeur services in Fréjus",
    services: [
      {
        title: "Nice airport transfers",
        text: "Fréjus ⇄ Nice Côte d'Azur airport in ~1 h via the A8, with flight tracking and a built-in margin.",
      },
      {
        title: "Saint-Raphaël TGV station shuttle",
        text: "A quick link to the neighbouring Saint-Raphaël-Valescure station for your TGV departures and arrivals.",
      },
      {
        title: "Chauffeur hire",
        text: "By the hour or by the day, between the Roman city, the Base Nature and Port-Fréjus.",
      },
      {
        title: "Roman heritage",
        text: "Arena, theatre, aqueduct and cathedral: drop-off as close as possible to the sites and pick-up after your visit.",
      },
      {
        title: "Long distance from Cannes/Nice",
        text: "Journeys from Cannes, Nice or the airport to Fréjus and the golfe, by saloon, van or VIP minibus.",
      },
    ],
  },
  locations: {
    heading: "Places & pick-up points",
    items: [
      {
        name: "Fréjus arena",
        description: "Roman amphitheatre on the edge of the old town.",
      },
      {
        name: "Episcopal complex",
        description:
          "Cathedral, cloister and baptistery at the heart of the city.",
      },
      {
        name: "Base Nature François Léotard",
        description: "Large seafront park, events and sport.",
      },
      {
        name: "Port-Fréjus & Fréjus-Plage",
        description: "Marina and beach, restaurants and seafront.",
      },
      {
        name: "Saint-Raphaël-Valescure station",
        description: "Neighbouring TGV station, minutes away.",
      },
      {
        name: "Nice airport",
        description: "Terminals 1 and 2, ~1 h via the A8.",
      },
    ],
  },
  generic: {
    heading: "A chauffeur in the Var's Roman city",
    subheading:
      "Two thousand years of history on one side, beaches and marina on the other: Fréjus is best explored without parking.",
    paragraphs: [
      "Once Forum Julii, founded in Roman times, Fréjus lines up an arena, an ancient theatre, an aqueduct and a remarkable episcopal complex before descending to Fréjus-Plage, Port-Fréjus and the vast Base Nature. KS Rent Services handles your journeys here with a private chauffeur, from the upper town to the seafront, without ever hunting for a space.",
      "Nice Côte d'Azur airport is around an hour away via the A8: as everywhere in the western Var, this journey is longer than from Cannes, so we allow a margin and track your flight. For the train, the nearby Saint-Raphaël-Valescure TGV station serves as the preferred transfer point.",
      "Between visits — the arena, the painted-ceiling cloister, the port — the chauffeur drops you as close as possible to the sites and collects you at the agreed time, ideal too for Base Nature events and seminars by van or VIP minibus.",
    ],
    bullets: [
      "Nice airport in ~1 h",
      "Saint-Raphaël TGV station shuttle",
      "Arena, Base Nature & Port-Fréjus",
    ],
  },
  process: {
    heading: "Book a chauffeur in Fréjus",
    steps: [
      "Tell us your route (airport, Saint-Raphaël station, Roman sites, beaches) and your times.",
      "We confirm the vehicle, the margin for Nice airport and the fixed package.",
      "The chauffeur tracks your flight or train and greets you on arrival.",
      "Chauffeur hire or drop-off in the heart of the city, pick-up on request.",
    ],
  },
  faq: [
    cityPriceFaq("en", "Fréjus"),
    {
      q: "How long to Nice airport?",
      a: "Around 1 hour via the A8. As Fréjus lies west of Nice, this transfer is longer than from Cannes: we allow a margin and track your flight.",
    },
    {
      q: "How do I reach the TGV station from Fréjus?",
      a: "The neighbouring Saint-Raphaël-Valescure station is just minutes away: we provide the dedicated shuttle for your TGV departures and arrivals.",
    },
    {
      q: "Do you serve the arena and the Roman sites?",
      a: "Yes, arena, ancient theatre, aqueduct and episcopal complex: the chauffeur drops you as close as possible and collects you after your visit.",
    },
    {
      q: "How far in advance should I book?",
      a: "Book as early as possible in summer and during Base Nature events; we also handle last-minute requests subject to availability.",
    },
    {
      q: "Do your chauffeurs speak English?",
      a: "Yes, our chauffeurs are bilingual French/English, used to an international clientele.",
    },
    {
      q: "Which vehicles for a group?",
      a: "7-seat vans and VIP minibuses for families, groups and seminars, alongside Mercedes E-Class and S-Class saloons.",
    },
  ],
  relatedLinks: [
    {
      label: "Private chauffeur in Saint-Tropez",
      href: "/en/chauffeur/saint-tropez",
    },
    { label: "Chauffeur hire Cannes", href: "/en/chauffeur/cannes" },
    {
      label: "Private chauffeur on the French Riviera",
      href: "/en/chauffeur/french-riviera",
    },
    {
      label: "Vehicle rental in Fréjus",
      href: "/en/location/car-rental-frejus",
    },
  ],
  cta: {
    title: "Your private chauffeur in Fréjus",
    text: "Nice airport transfer, TGV station shuttle or chauffeur hire in the Roman city: fast reply, 7 days a week.",
    primaryHref: "/en/reservation",
    primaryLabel: "Book a chauffeur",
  },
  seo: {
    title: "Private chauffeur in Fréjus 24/7 — car service & transfers",
    description:
      "Private car service in Fréjus: Nice airport transfers (~1 h), Saint-Raphaël TGV station shuttle, the Roman city, Base Nature and Port-Fréjus.",
    canonical: "/en/chauffeur/frejus",
  },
};

export default frejusEn;
